/**
 * Convex-backed time entries.
 *
 * Keeps the snake_case shape the timer UI already reads, so the swap stays
 * confined to this file (same pattern as convex-tasks / convex-projects).
 */
import { api } from '@/convex/_generated/api'
import type { Doc, Id } from '@/convex/_generated/dataModel'
import { convex, whenConvexReady } from '@/lib/convex-client'

export interface TimeEntryRecord {
  id: string
  task_id: string
  start_time: string
  end_time: string | null
  duration: number | null
  notes: string | null
  created_at: string
  /**
   * Resolved by the server, and null only when the task was deleted outright.
   * Joining this in the browser used to lose the name of every archived task's
   * sessions, because the client only holds the active list.
   */
  task_title?: string | null
}

function toRecord(doc: Doc<'timeEntries'> & { taskTitle?: string | null }): TimeEntryRecord {
  return {
    id: doc._id,
    task_id: doc.taskId,
    start_time: new Date(doc.startTime).toISOString(),
    end_time: doc.endTime ? new Date(doc.endTime).toISOString() : null,
    duration: doc.duration ?? null,
    notes: doc.notes ?? null,
    created_at: new Date(doc._creationTime).toISOString(),
    task_title: doc.taskTitle ?? null,
  }
}

export async function getTimeEntries(): Promise<TimeEntryRecord[]> {
  await whenConvexReady()
  const docs = await convex.query(api.timeEntries.list, {})
  return docs.map(toRecord)
}

export async function getTimeEntriesForTask(taskId: string): Promise<TimeEntryRecord[]> {
  await whenConvexReady()
  const docs = await convex.query(api.timeEntries.listForTask, {
    taskId: taskId as Id<'tasks'>,
  })
  return docs.map(toRecord)
}

export async function createTimeEntry(entry: {
  task_id: string
  start_time: string
  end_time?: string | null
  duration?: number | null
  notes?: string | null
}): Promise<TimeEntryRecord> {
  await whenConvexReady()
  const doc = await convex.mutation(api.timeEntries.create, {
    taskId: entry.task_id as Id<'tasks'>,
    startTime: Date.parse(entry.start_time),
    endTime: entry.end_time ? Date.parse(entry.end_time) : undefined,
    duration: entry.duration ?? undefined,
    notes: entry.notes ?? undefined,
  })

  return toRecord(doc)
}

/**
 * Corrects a recorded session.
 *
 * `duration` is what the learning loop reads, so it is passed explicitly rather
 * than inferred from the endpoints — a caller that moves a session without
 * restating its length should not silently change how long it took.
 */
export async function updateTimeEntry(
  entryId: string,
  updates: {
    start_time?: string
    end_time?: string | null
    duration?: number
    notes?: string | null
  },
): Promise<TimeEntryRecord> {
  await whenConvexReady()

  const doc = await convex.mutation(api.timeEntries.update, {
    entryId: entryId as Id<'timeEntries'>,
    startTime: updates.start_time ? Date.parse(updates.start_time) : undefined,
    endTime: updates.end_time ? Date.parse(updates.end_time) : undefined,
    duration: updates.duration,
    notes: updates.notes ?? undefined,
  })

  return toRecord(doc)
}

export async function deleteTimeEntry(entryId: string): Promise<void> {
  await whenConvexReady()
  await convex.mutation(api.timeEntries.remove, { entryId: entryId as Id<'timeEntries'> })
}

/** Live time-entry subscription; mirrors subscribeToTasks. */
export function subscribeToTimeEntries(
  onEntries: (entries: TimeEntryRecord[]) => void,
): () => void {
  let unsubscribe: (() => void) | null = null
  let cancelled = false

  void whenConvexReady().then(() => {
    if (cancelled) return
    const watch = convex.watchQuery(api.timeEntries.list, {})
    unsubscribe = watch.onUpdate(() => {
      const docs = watch.localQueryResult()
      if (docs) onEntries(docs.map(toRecord))
    })
  })

  return () => {
    cancelled = true
    unsubscribe?.()
  }
}
